import { buildShippingPayload } from './buildShippingPayload';
import { roundTo2Decimals } from './unitPriceInput';
import { serializeEmailList } from '@/utils/emailList';
import { deliveryAddressName } from '../components/CustomerSidebar/addressBook';
import {
  effectiveShippingMode,
  type FulfillmentChoice,
  type ShippingModeValue,
} from '../components/Fulfillment/fulfillmentChoice';
import type {
  CommissionStaff,
  CreatePosOrderPayload,
  FulfillmentMode,
  OrderItem,
  OrderType,
  PaymentMethod,
  UserType,
} from '../types';

export interface CreateOrderPayloadInput {
  /**
   * Snapshot form store đọc bằng `getFieldsValue(true)` — field khách/hoá đơn nằm
   * trong `CustomerDialog` lazy-mount, đọc không tham số sẽ ra rỗng.
   *
   * `customer_name` / `citizen_id` phải đã đi qua `deriveDeliverySnapshot` trước
   * khi truyền vào (POSPage làm việc đó vì nó còn dùng lại `values.customer_name`
   * cho dialog thành công).
   *
   * Task 7b fix round 1: hàm này TỰ gọi `buildShippingPayload` từ chính `values`
   * (xem thân hàm) — không còn nhận `shippingPayload` dựng sẵn từ ngoài. Lý do:
   * bên gọi (`POSPage`) có thể dựng `shippingPayload` bằng `values.shipping_mode`
   * THÔ (rỗng khi thu ngân chưa bấm thanh PTNH), trong khi đơn giao hàng vẫn phải
   * được coi là `'self'` (`effectiveShippingMode`). Nhận `shippingPayload` sẵn từ
   * ngoài là nhận luôn cái rỗng đó — thiếu `shipping_fee_payer`/`shipping_fee`/
   * `prepaid_amount` và làm `cash_received` rẽ nhầm nhánh. Tự dựng lại ở đây từ
   * `values` + mode đã chuẩn hoá thì luôn đúng, bất kể bên gọi có nhớ chuẩn hoá
   * trước hay không.
   */
  values: Record<string, unknown>;
  /** Giỏ hàng ĐÃ validate — cùng snapshot mà `validatePosItems` đã duyệt. */
  orderItems: OrderItem[];
  userType: UserType;
  receiveMethod: 'delivery' | 'pickup' | 'vault';
  /** Đã qua luật "trả trước = 0 thì ép về cash" (spec §5.2 / D15). */
  effectivePaymentMethod: PaymentMethod;
  paymentAccountId?: string;
  /** Phần thu qua phương thức phi tiền mặt (`nonCashNow` ở POSPage). */
  nonCashAmount: number;
  /** Số quầy THỰC THU lúc này: trả trước với đơn giao hàng, cả `payable` với đơn quầy. */
  amountCollectedNow: number;
  /** Tiền khách đưa — chỉ đơn bán tại quầy có khái niệm này. */
  cashReceived?: number;
  loyaltyRedeemValue: number;
  voucherCodes: string[];
  voucherSessionId: string;
  /** Snapshot from POS promotion evaluation; the BE uses this to re-price the order. */
  promotionSnapshotId?: string;
  idempotencyKey?: string;
  /** TCR-1963 — true ⇒ payload có `save_as_draft: true` (BE ghi đơn ở trạng thái Nháp, không thu tiền). */
  saveAsDraft?: boolean;
  tradeDiscount: number;
  /** TCR-977: phụ thu cấp đơn — 0 nếu thu ngân không nhập gì. Task 6 will wire this from form. */
  surcharge?: number;
  fulfillmentMode: FulfillmentMode;
  orderType: OrderType;
  liveSessionId?: string;
  commissionStaff: CommissionStaff[];
  defaultFulfillmentChoice: FulfillmentChoice;
}

/**
 * Ngày sinh: `DateMaskInput` cho ra `DD/MM/YYYY`, BE cần `YYYY-MM-DD`.
 *
 * Vẫn chấp nhận cả object kiểu dayjs để lỡ nguồn input đổi về DatePicker thì đơn
 * không âm thầm mất ngày sinh.
 */
export function toIsoBirthday(raw: unknown): string | undefined {
  if (!raw) return undefined;
  if (typeof raw === 'string') {
    const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : undefined;
  }
  if (typeof raw === 'object' && 'format' in (raw as object)) {
    return (raw as { format(fmt: string): string }).format('YYYY-MM-DD');
  }
  return undefined;
}

/**
 * Dựng body `POST /pos/orders`.
 *
 * Tách khỏi `POSPage.handleCheckout` vì đây là phần DÀI nhất mà lại thuần nhất:
 * chỉ ánh xạ field, không quyết định gì. Để lẫn trong hàm checkout thì mọi thay
 * đổi luật tiền (thứ thật sự cần đọc kỹ) bị chôn giữa ~85 dòng ánh xạ.
 *
 * KHÔNG tự tính tiền: `amountCollectedNow`, `nonCashAmount`,
 * `effectivePaymentMethod` do POSPage quyết định rồi truyền vào — chúng là các
 * quyết định có comment giải thích riêng ở đó và còn được dùng lại cho hoá đơn
 * in. Tính lại ở đây là mở đường cho hai con số lệch nhau.
 *
 * Field vận chuyển đi qua `...shippingPayload` (đã lọc theo `receive_method` +
 * `shipping_mode` HIỆU LỰC — xem `effectiveShippingMode` bên dưới), KHÔNG liệt
 * kê tay ở đây.
 */
export function buildCreateOrderPayload({
  values,
  orderItems,
  userType,
  receiveMethod,
  effectivePaymentMethod,
  paymentAccountId,
  nonCashAmount,
  amountCollectedNow,
  cashReceived,
  loyaltyRedeemValue,
  voucherCodes,
  voucherSessionId,
  promotionSnapshotId,
  idempotencyKey,
  saveAsDraft,
  tradeDiscount,
  surcharge = 0,
  fulfillmentMode,
  orderType,
  liveSessionId,
  commissionStaff,
  defaultFulfillmentChoice,
}: CreateOrderPayloadInput): CreatePosOrderPayload {
  const isDelivery = receiveMethod === 'delivery';

  // Task 7b fix round 1 — CRITICAL: `effectiveShippingMode` tính MỘT LẦN ở đây,
  // rồi dùng cho CẢ BA chỗ bên dưới — `hasShipping`, tham số mode truyền vào
  // `buildShippingPayload`, và `shipping_mode` cuối cùng trong `shippingPayload`
  // (`buildShippingPayload` tự gán key đó khi mode có giá trị). TRƯỚC ĐÂY hàm
  // này nhận `shippingPayload` dựng sẵn từ ngoài (bằng mode THÔ) rồi chỉ ghi đè
  // riêng key `shipping_mode` sau khi object đã dựng xong — sai với đúng kịch
  // bản Task 7b vừa hợp thức hoá (đơn giao hàng, thu ngân chưa bấm gì vào thanh):
  // `values.shipping_mode` rỗng ⇒ `buildShippingPayload` với mode thô trả `{}`
  // (KHÔNG có `shipping_fee_payer`/`shipping_fee`/`prepaid_amount`), và
  // `hasShipping` (thô) = false khiến `cash_received` rẽ nhầm sang `cashReceived`
  // (rác còn sót từ lúc tab ở chế độ bán tại quầy) thay vì `amountCollectedNow`
  // (số ĐÃ thu thật). Gọi lại `buildShippingPayload` NGAY TẠI ĐÂY với mode đã
  // chuẩn hoá thì cả 12 field vận chuyển lẫn `hasShipping` đều đúng ngay từ đầu,
  // không phụ thuộc bên gọi có nhớ chuẩn hoá trước hay không.
  const effectiveMode = effectiveShippingMode(
    receiveMethod,
    values.shipping_mode as ShippingModeValue | undefined,
    defaultFulfillmentChoice,
  );
  const shippingPayload = buildShippingPayload({
    receiveMethod,
    values: { ...values, shipping_mode: effectiveMode },
  });
  const hasShipping = effectiveMode !== undefined;
  /** Chỉ đơn giao hàng mới gửi các field người nhận — xem `receive_method`. */
  const forDelivery = <T,>(v: T): T | undefined => (isDelivery ? v : undefined);

  return {
    customer_id: values.customer_user_id as string | undefined,
    customer_name: values.customer_name as string,
    customer_phone: values.customer_phone as string,
    customer_address: values.customer_address as string | undefined,
    customer_city_id: values.customer_city_id as string,
    customer_ward_id: values.customer_ward_id as string | undefined,
    customer_email: values.customer_email as string | undefined,
    customer_birthday: toIsoBirthday(values.customer_birthday),
    description: values.description as string | undefined,
    citizen_id: values.citizen_id as string,
    care_staff_id: values.care_staff_id as string | undefined,
    delivery_days: values.delivery_days as number | undefined,
    user_type: userType,

    receive_method: receiveMethod,
    pickup_store_id: isDelivery ? undefined : (values.pickup_store_id as string | undefined),
    delivery_name: forDelivery(values.delivery_name as string | undefined),
    delivery_phone: forDelivery(values.delivery_phone as string | undefined),
    delivery_citizen_id: forDelivery(values.delivery_citizen_id as string | undefined),
    delivery_email: forDelivery(values.delivery_email as string | undefined),
    delivery_city_id: forDelivery(values.delivery_city_id as string | undefined),
    delivery_ward_id: forDelivery(values.delivery_ward_id as string | undefined),
    delivery_address: forDelivery(values.delivery_address as string | undefined),
    delivery_note: forDelivery(values.delivery_note as string | undefined),
    delivery_address_name: forDelivery(deliveryAddressName(values) || undefined),
    // `shippingPayload` ở đây ĐÃ được dựng từ mode hiệu lực (xem đầu hàm) — không
    // còn ghi đè riêng key `shipping_mode` sau khi object đã dựng xong nữa (fix
    // round 1: override một key trên object đã dựng xong là chỉ vá đúng một
    // triệu chứng, bỏ sót `shipping_fee_payer`/`shipping_fee`/`prepaid_amount`).
    ...shippingPayload,

    invoice_name: values.invoice_name as string,
    invoice_tax_id: values.invoice_tax_id as string,
    invoice_address: values.invoice_address as string,
    invoice_email: serializeEmailList(values.invoice_email as string | undefined) || undefined,
    buyer_name: values.buyer_name as string | undefined,
    buyer_cccd: values.buyer_cccd as string,
    amis_account_object_id: values.amis_account_object_id as string | undefined,

    items: orderItems.map((item) => ({
      // BE tách `variant_code_serial` theo '_' → parts[0]=variant_code, parts[1]=serial.
      // Hàng serial phải gửi `<variantCode>_<serial>`, KHÔNG gửi serial trần.
      variant_code_serial: item.specificationCode
        ? `${item.variantCode}_${item.specificationCode}`
        : item.variantCode,
      quantity: item.quantity,
      // TCR-2224 — BE 400 khi >2 chữ số lẻ; giá từ catalog/bảng giá có thể mang nhiều lẻ hoặc sai số dấu phẩy động.
      price: roundTo2Decimals(item.price),
      applied_price: roundTo2Decimals(item.price),
      discount_type: item.discountType ?? 'percent',
      discount_percent: item.discountPercent,
      // TCR-1257: `item.discountAmount` trong TOÀN BỘ state POS (orderItemCalc.ts
      // recalcItem/computePosTotals, OrderItemsTable.handleCkAmtBlur) đã LÀ tổng
      // tiền CK của CẢ DÒNG (không phải đơn giá) — xem comment tại các nơi đó.
      // BE (pos-order-pricing.service.ts) cũng nhận `discount_amount` đúng với
      // nghĩa TỔNG DÒNG: so sánh trực tiếp với `appliedRaw * quantity` rồi gán
      // thẳng `discountAmount = Math.round(requestedLineDiscountAmount)` làm CK
      // tổng dòng cuối cùng — KHÔNG nhân lại quantity. Bản cũ ở đây chia cho
      // quantity trước khi gửi (dựa trên giả định sai là BE sẽ nhân lại), khiến
      // CK thực tế áp dụng chỉ bằng 1/quantity số tiền cashier đã nhập/nhìn thấy
      // trên màn hình — khách bị tính tiền cao hơn hiển thị với mọi dòng CK kiểu
      // "amount" có quantity > 1. Gửi thẳng, không chia, để khớp 1-1 với BE.
      discount_amount: item.discountAmount,
      override_reason: item.overrideReason || undefined,
      ...(item.vatOverride !== undefined ? { vat_rate: item.vatOverride } : {}),
      seller_staff_id: item.sellerStaffId,
      // [BỎ TRƯỜNG CHỦ KHO] không gửi warehouse_staff_id nữa (cột C.Kho đã gỡ).
      // BE đã tắt @Validate(WarehouseStaffRequiredForInstantConstraint) tương ứng.
      // warehouse_staff_id: item.warehouseStaffId,
      // TCR-629: kho xuất user chọn (line quantity). Bỏ trống → BE tự resolve.
      warehouse_id: item.warehouseId,
      // Combo: chỉ gửi ĐỊNH DANH. BE tự verify và tự tính giá — mọi con số tiền
      // gửi kèm ở đây đều bị BE bỏ qua (spec §3.1).
      combo_group_id: item.comboGroupId,
      combo_id: item.comboId,
      // Ngoại lệ DUY NHẤT, và nó không phá luật trên: đây là ASSERTION, không
      // phải giá. Ta khai "màn hình đã hiển thị số này"; BE vẫn tự dựng giá từ
      // combo_definitions và chỉ dùng số này để TỪ CHỐI khi hai bên lệch nhau.
      // Không có nó, một lần admin sửa combo_price giữa ca là đơn được ghi ở
      // giá mới trong khi thu ngân đã thu giá cũ (I1 / spec §7 dòng 1).
      combo_unit_price: item.comboGroupId ? item.comboUnitPrice : undefined,
      combo_applied_unit_price: item.comboGroupId
        ? (item.comboAppliedUnitPrice ?? item.comboUnitPrice)
        : undefined,
      combo_discount_type: item.comboGroupId ? (item.comboDiscountType ?? 'percent') : undefined,
      combo_discount_percent: item.comboGroupId ? (item.comboDiscountPercent ?? 0) : undefined,
      combo_discount_amount: item.comboGroupId ? (item.comboDiscountAmount ?? 0) : undefined,
      // TCR-1074: đánh dấu dòng quà tặng để BE build order.summary.promotion_gifts.
      gift_promotion_id: item.gift_promotion_id,
      // TCR-1143: đánh dấu dòng mua theo giá flash sale — BE dùng field này để
      // reserve/confirm/release đúng FlashSaleItemV2 (lifecycle đã có sẵn,
      // trước bản này field luôn undefined vì không nơi nào populate).
      // Xem chú thích ở `buildEvaluateItems` — hai nguồn cùng mang id item.
      flash_sale_item_id: item.flash_sale_item_id ?? item.flash_sale_id,
    })),

    payment_method: effectivePaymentMethod,
    payment_account_id: effectivePaymentMethod === 'cash' ? undefined : paymentAccountId,
    non_cash_amount: nonCashAmount,
    cash_amount: Math.max(0, amountCollectedNow - nonCashAmount),
    cash_received: hasShipping ? amountCollectedNow : cashReceived,
    pricing_method: 'other',
    fulfillment_type: fulfillmentMode,
    order_type: orderType,
    live_session_id: liveSessionId,
    commission_staff: commissionStaff.length > 0 ? commissionStaff : undefined,

    // Promotion fields
    loyalty_redeem_points: (values.loyalty_redeem_points as number) || undefined,
    loyalty_redeem_value: loyaltyRedeemValue || undefined,
    applied_voucher_codes: voucherCodes.length ? voucherCodes : undefined,
    // Only the BE-supported snapshot crosses checkout. Preview discount fields
    // stay UI-only so the promotion engine, not the client, owns final pricing.
    snapshot_id: promotionSnapshotId,
    ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    ...(saveAsDraft ? { save_as_draft: true } : {}),
    voucher_session_id: voucherSessionId,
    trade_discount_amount: tradeDiscount > 0 ? tradeDiscount : undefined,
    surcharge_amount: (surcharge ?? 0) > 0 ? surcharge : undefined,
    // Task 18: dịch vụ mang thuế suất cho dòng Phụ thu — bỏ qua khi trống
    // (thu ngân gõ tự do, không chọn dịch vụ nào) hoặc khi không có phụ thu.
    surcharge_service_id:
      (surcharge ?? 0) > 0 && values.surcharge_service_id
        ? (values.surcharge_service_id as string)
        : undefined,
  };
}
